import React, { useState, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { CoercedSelectValue } from '../../prop-types';
import QuantityButton from './button';

const handleInputSelect = (e) => {
  e.preventDefault();
};

const handleInputFocus = (e, value) => {
  if (value === 0) {
    e.target.select();
  }
};

const handleDecrementClick = (onChange, currentValue) => {
  onChange({
    target: {
      value: (parseInt(currentValue, 10) || 0) - 1,
    },
  });
};

const handleIncrementClick = (onChange, currentValue) => {
  onChange({
    target: {
      value: (parseInt(currentValue, 10) || 0) + 1,
    },
  });
};

const isValidNumber = (val) => {
  const number = Number(val);
  return Number.isInteger(number);
};

const NumberInputField = (props) => {
  const {
    noButtons,
    label,
    disabled,
    value: propValue,
    id,
    min,
    max,
    onChange,
    inputClassName,
  } = props;
  const [inputValue, setInputValue] = useState(propValue);

  // only update immediately if the value is a valid, positive number
  const handleChange = useCallback(
    (e) => {
      const {
        target: { value },
      } = e;

      setInputValue(value);
      if (isValidNumber(value) && Number(value) > 0) {
        onChange(e);
      }
    },
    [onChange],
  );

  // update the value if the input is blurred only if it's a valid number, zero or above
  // otherwise reset the input to the prop value
  // ie only a zero will actually remove the photo size
  const handleBlur = useCallback(
    (e) => {
      const {
        target: { value },
      } = e;
      const inputValueNotBlank = String(value).trim().length > 0;

      if (isValidNumber(value) && Number(value) >= 0 && inputValueNotBlank) {
        onChange(e);
      } else {
        setInputValue(propValue);
      }
    },
    [onChange, propValue],
  );

  // ui is rendered with blank values then gets updated, and size quantity can be updated
  // from other controls
  useEffect(() => {
    setInputValue(propValue);
  }, [propValue]);

  return (
    <div
      className={classNames('input-group number-field flex-nowrap', {
        'no-buttons': noButtons,
      })}>
      <span className="input-group-prepend">
        <QuantityButton
          ariaLabel={`${label} print, ${inputValue}, decrease quantity`}
          className="less"
          disabled={disabled || inputValue <= min}
          inputID={id}
          max={max}
          min={min}
          value={inputValue}
          onClick={() => handleDecrementClick(onChange, inputValue)}>
          <i className="fa fa-minus" />
        </QuantityButton>
      </span>
      <input
        id={id}
        aria-atomic="false"
        aria-label={`${label}, quantity ${inputValue}`}
        aria-live="assertive"
        aria-valuemax={max}
        aria-valuemin={min}
        aria-valuenow={inputValue}
        className={inputClassName}
        type="number"
        value={inputValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={(e) => handleInputFocus(e, inputValue)}
        onSelect={(e) => handleInputSelect(e)}
        disabled={disabled}
      />
      <span className="input-group-append">
        <QuantityButton
          ariaLabel={`${label} print, ${inputValue}, increase quantity`}
          className="more"
          disabled={disabled || inputValue >= max}
          inputID={id}
          max={max}
          min={min}
          value={inputValue}
          onClick={() => handleIncrementClick(onChange, inputValue)}>
          <i className="fa fa-plus" />
        </QuantityButton>
      </span>
    </div>
  );
};

NumberInputField.propTypes = {
  noButtons: PropTypes.bool,
  label: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  value: CoercedSelectValue.isRequired,
  id: PropTypes.string.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  inputClassName: PropTypes.string,
};

NumberInputField.defaultProps = {
  noButtons: false,
  label: undefined,
};

export default NumberInputField;
